import { makeObservable, observable, action } from 'mobx';
import { SingletonClass } from '@/app/utils/SingletonClass';
import { DialogButton } from '@/app/shared/ui/dialog/Dialog';

/**
 * Контроллер для управления диалоговыми окнами
 * Использует MobX для управления состоянием
 * Заменяет функциональность useDialog хука
 */
export class DialogController extends SingletonClass {
    private static _instance: DialogController;

    // MobX observable состояние
    isOpen = false;
    title?: string;
    message = '';
    buttons?: DialogButton[];
    closable = true;

    /** Вызывается при любом closeDialog (кнопки, ESC, оверлей) */
    private onDismiss: (() => void) | null = null;

    static getInstance(): DialogController {
        if (!DialogController._instance) {
            DialogController._instance = new DialogController();
        }
        return DialogController._instance;
    }

    constructor() {
        super();
        makeObservable(this, {
            isOpen: observable,
            title: observable,
            message: observable,
            buttons: observable,
            closable: observable,
            showDialog: action,
            showErrorDialog: action,
            showSuccessDialog: action,
            closeDialog: action,
        });
    }

    /**
     * Показывает диалог с заданным сообщением и опциями
     */
    showDialog(
        message: string,
        options?: {
            title?: string;
            buttons?: DialogButton[];
            closable?: boolean;
            /** Сброс флагов подписчиков при закрытии диалога любым способом */
            onDismiss?: () => void;
        }
    ): void {
        this.message = message;
        this.title = options?.title;
        this.buttons = options?.buttons;
        this.closable = options?.closable !== false;
        this.onDismiss = options?.onDismiss ?? null;
        this.isOpen = true;
    }

    /**
     * Показывает диалог с ошибкой
     */
    showErrorDialog(
        message: string,
        options?: {
            title?: string;
            onRetry?: () => void;
            onClose?: () => void;
            onDismiss?: () => void;
        }
    ): void {
        const buttons: DialogButton[] = [];
        
        if (options?.onRetry) {
            buttons.push({
                label: 'Повторить',
                onClick: () => {
                    this.closeDialog();
                    options.onRetry?.();
                },
                variant: 'primary',
            });
        }
        
        buttons.push({
            label: options?.onRetry ? 'Закрыть' : 'OK',
            onClick: () => {
                this.closeDialog();
                options?.onClose?.();
            },
            variant: options?.onRetry ? 'secondary' : 'primary',
        });

        this.message = message;
        this.title = options?.title || 'Ошибка';
        this.buttons = buttons;
        this.closable = true;
        this.onDismiss = options?.onDismiss ?? null;
        this.isOpen = true;
    }

    /**
     * Показывает диалог с успешным сообщением
     */
    showSuccessDialog(
        message: string,
        options?: {
            title?: string;
            onClose?: () => void;
            onDismiss?: () => void;
        }
    ): void {
        this.message = message;
        this.title = options?.title || 'Успешно';
        this.onDismiss = options?.onDismiss ?? null;
        this.buttons = [
            {
                label: 'OK',
                onClick: () => {
                    this.closeDialog();
                    options?.onClose?.();
                },
                variant: 'primary',
            },
        ];
        this.closable = true;
        this.isOpen = true;
    }

    /**
     * Закрывает диалог
     * Используется стрелочная функция для сохранения контекста this при передаче как callback
     */
    closeDialog = (): void => {
        this.isOpen = false;
        const d = this.onDismiss;
        this.onDismiss = null;
        d?.();
    }
}
